import {
    faCalendarDays,
    faChevronDown,
    faChevronUp,
    faLayerGroup,
    faMagnifyingGlass
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Period } from '@spike/model';
import Button from 'components/UI/V3/Button/Button';
import { DateRangePickerDropdown } from 'components/UI/V3/DateRangePickerDropdown';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownFooter } from 'components/UI/V3/Dropdown/Footer/DropdownFooter';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { Spinner } from 'components/UI';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Pagination, PaginationProps } from 'components/UI/V3/Pagination/Pagination';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';
import useTimeZone from 'hooks/useTimeZone';
import moment from 'moment-timezone';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import {
    activityLogCategoriesMatch,
    getActivityLogCategoryApiValue,
    getActivityLogCategoryDisplayLabel
} from '../../clientOverviewUtils';
import styles from './ClientOverviewActivityLog.module.scss';

export interface ClientOverviewActivityLogItem {
    date: string;
    event: string;
    category: string;
}

interface ClientOverviewActivityLogProps {
    activityLog: ClientOverviewActivityLogItem[];
    searchText: string;
    searchInputKey?: string | number;
    datePeriod: Period | null;
    categories: string[];
    selectedCategories: string[] | undefined;
    pagination: PaginationProps;
    onSearchChange: (value: string) => void;
    onDatePeriodChange: (period: Period | null) => void;
    onCategoriesChange: (categories: string[] | undefined) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
}

export const ClientOverviewActivityLog: FunctionComponent<ClientOverviewActivityLogProps> = props => {
    const [expanded, setExpanded] = useState(true);
    const [isCategorySelectorOpen, setIsCategorySelectorOpen] = useState(false);
    const [selectedCategoriesState, setSelectedCategoriesState] = useState<string[] | undefined>(
        props.selectedCategories
    );
    const timeZone = useTimeZone();
    const { todayOption, thisMonthOption, last30DaysOption, lastMonthOption, allTimeOption } = useDateRangeOptions();

    const dateRangeOptions = useMemo(
        () => [todayOption, thisMonthOption, last30DaysOption, lastMonthOption, allTimeOption],
        [todayOption, thisMonthOption, last30DaysOption, lastMonthOption, allTimeOption]
    );

    const maxDate = useMemo(() => moment().tz(timeZone).endOf('day'), [timeZone]);

    const categoryLabel = useMemo(() => {
        if (props.selectedCategories === undefined || props.selectedCategories.length === props.categories?.length) {
            return 'All Activity';
        }

        return `Activity (${props.selectedCategories.length})`;
    }, [props.selectedCategories, props.categories?.length]);

    useEffect(() => {
        if (!isCategorySelectorOpen) {
            setSelectedCategoriesState(props.selectedCategories ?? undefined);
        }
    }, [isCategorySelectorOpen, props.selectedCategories]);

    const toggleAllCategoriesHandler = useCallback(() => {
        if (selectedCategoriesState?.length === 0) {
            setSelectedCategoriesState([...props.categories]);
        } else {
            setSelectedCategoriesState([]);
        }
    }, [selectedCategoriesState, props.categories]);

    const selectCategoryHandler = useCallback(
        (category: string) => {
            const apiCategory = getActivityLogCategoryApiValue(category);

            if (selectedCategoriesState === undefined) {
                setSelectedCategoriesState(
                    props.categories.filter(item => !activityLogCategoriesMatch(item, apiCategory))
                );
            } else if (selectedCategoriesState.some(item => activityLogCategoriesMatch(item, apiCategory))) {
                setSelectedCategoriesState(prev =>
                    prev?.filter(item => !activityLogCategoriesMatch(item, apiCategory))
                );
            } else {
                setSelectedCategoriesState(prev => [...(prev ?? []), apiCategory]);
            }
        },
        [selectedCategoriesState, props.categories]
    );

    const applyCategoriesHandler = useCallback(() => {
        const allSelected =
            selectedCategoriesState === undefined || selectedCategoriesState.length === props.categories?.length;

        props.onCategoriesChange(allSelected ? undefined : selectedCategoriesState);
        setIsCategorySelectorOpen(false);
    }, [selectedCategoriesState, props.categories, props.onCategoriesChange]);

    return (
        <div className={styles.root}>
            <button
                type="button"
                className={styles.activityHeader}
                aria-expanded={expanded}
                onClick={() => setExpanded(current => !expanded)}
            >
                <span>Activity Log</span>
                <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
            </button>

            {expanded && (
                <>
                    <div className={styles.activityToolbar}>
                        <div className={styles.search}>
                            <Input
                                key={props.searchInputKey}
                                value={props.searchText}
                                placeholder="Search by activity"
                                startIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                                autoFocus={false}
                                className={styles.searchInput}
                                onChange={event => props.onSearchChange(event.target.value)}
                            />
                        </div>
                        <div className={styles.filters}>
                            <Dropdown
                                open={isCategorySelectorOpen}
                                onOpenChange={setIsCategorySelectorOpen}
                            >
                                <DropdownTrigger className={styles.categorySelector}>
                                    <Button
                                        label={categoryLabel}
                                        size="large"
                                        variant="outlined"
                                        leftIcon={faLayerGroup}
                                        className={styles.filterButton}
                                    />
                                </DropdownTrigger>

                                <DropdownContent
                                    maxHeight={300}
                                    sideOffset={12}
                                    align="end"
                                    className={styles.categoryDropdown}
                                >
                                    <DropdownItem
                                        type="checkbox"
                                        checked={
                                            selectedCategoriesState === undefined ||
                                            selectedCategoriesState.length === props.categories?.length
                                        }
                                        onSelect={toggleAllCategoriesHandler}
                                    >
                                        All Activity
                                    </DropdownItem>

                                    <hr className={styles.categoryDivider} />

                                    {props.categories.map(category => (
                                        <DropdownItem
                                            key={category}
                                            type="checkbox"
                                            checked={
                                                selectedCategoriesState === undefined ||
                                                selectedCategoriesState.some(item =>
                                                    activityLogCategoriesMatch(item, category)
                                                )
                                            }
                                            onSelect={() => selectCategoryHandler(category)}
                                        >
                                            {getActivityLogCategoryDisplayLabel(category)}
                                        </DropdownItem>
                                    ))}

                                    <DropdownFooter>
                                        <Button
                                            label="Apply"
                                            variant="green"
                                            onClick={applyCategoriesHandler}
                                            disabled={selectedCategoriesState?.length === 0}
                                        />
                                    </DropdownFooter>
                                </DropdownContent>
                            </Dropdown>
                            <DateRangePickerDropdown
                                timeZone={timeZone}
                                value={props.datePeriod}
                                maxDate={maxDate}
                                options={dateRangeOptions}
                                buttonProps={{
                                    size: 'large',
                                    leftIcon: faCalendarDays,
                                    variant: 'outlined',
                                    className: styles.dateRangeButton
                                }}
                                dropdownProps={{ className: styles.dateRangeDropdown }}
                                onChange={props.onDatePeriodChange}
                            />
                        </div>
                    </div>

                    {props.pagination.isLoading ? (
                        <div className={styles.loading}>
                            <Spinner />
                        </div>
                    ) : (
                        <>
                            <div className={styles.tableWrapper}>
                                <table className={styles.activityTable}>
                                    <thead>
                                        <tr>
                                            <th>Date & Time</th>
                                            <th>Event</th>
                                            <th>Category</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {props.activityLog.map(activity => (
                                            <tr key={`${activity.date}-${activity.event}`}>
                                                <td>{activity.date}</td>
                                                <td>{activity.event}</td>
                                                <td>{getActivityLogCategoryDisplayLabel(activity.category)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {props.activityLog.length > 0 && (
                                <Pagination
                                    page={props.pagination?.page || 1}
                                    totalPages={props.pagination?.totalPages || 1}
                                    size={props.pagination?.size || 10}
                                    isLoading={props.pagination.isLoading}
                                    className={styles.pagination}
                                    onPageChange={props.onPageChange}
                                    onSizeChange={props.onPageSizeChange}
                                />
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ClientOverviewActivityLog;
